import {Injectable} from '@angular/core';
import {API_BASE_URL} from './env.service';
import {HttpClient} from '@angular/common/http';
import {map, Observable, tap, of} from 'rxjs';
import {AssignRolesRequest} from 'src/shared/models/fga/role';
import {PermissionQueryParams, PermissionResponse} from 'src/shared/models/fga/permission';
import {ObjectId, ObjectType} from 'src/shared/models/object.model';
import {ObjectGrantee} from '../../shared/models/fga/grantees';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private permissionsCache = new Map<string, boolean>();

  constructor(
    private http: HttpClient,
  ) {
  }

  /**
   * Generates a unique cache key for a given PermissionQueryParams
   */
  private getCacheKey(params: PermissionQueryParams): string {
    return [
      params.UserID,
      params.Permissions.join(','),
      params.ObjectID ?? '',
      params.ObjectType ?? ''
    ].join('|');
  }

  queryPermissions(params: PermissionQueryParams): Observable<PermissionResponse> {
    const permissionsString = params.Permissions.join(',');

    const queryParams: any = {
      UserID: params.UserID,
      Permissions: permissionsString,
    };

    if (params.ObjectID) {
      queryParams.ObjectID = params.ObjectID;
    }

    if (params.ObjectType) {
      queryParams.ObjectType = params.ObjectType;
    }

    return this.http.get<PermissionResponse>(`${API_BASE_URL}/permissions`, {
      params: queryParams,
    });
  }

  hasPermissions(params: PermissionQueryParams): Observable<boolean> {
    const cacheKey = this.getCacheKey(params);
    if (this.permissionsCache.has(cacheKey)) {
      return of(this.permissionsCache.get(cacheKey));
    }
    return this.queryPermissions(params).pipe(
      map(response => response.Results.every(result => result.Allowed)),
      tap(result => this.permissionsCache.set(cacheKey, result))
    );
  }

  /**
   * Clears the permissions cache (e.g., on logout)
   */
  clearCache(): void {
    this.permissionsCache.clear();
  }

  queryObjectGrantees(objectId: ObjectId, objectType: ObjectType): Observable<ObjectGrantee[]> {
    return this.http.get<ObjectGrantee[]>(`${API_BASE_URL}/grantees`, {
      params: {
        ObjectID: objectId,
        ObjectType: objectType,
      },
    });
  }

  assignRolesToUser(params: AssignRolesRequest): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/grantee-roles`, params);
  }

  /**
   * Sets the final state of roles for a user in relation to an object.
   * This will replace all existing roles with the provided roles.
   */
  setUserRoles(params: AssignRolesRequest): Observable<void> {
    return this.http.put<void>(`${API_BASE_URL}/grantee-roles`, params);
  }

  unassignRolesFromUser(params: AssignRolesRequest): Observable<void> {
    return this.http.delete<void>(`${API_BASE_URL}/grantee-roles`, { body: params });
  }
}
